import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  InputAdornment
} from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { httpRequest } from 'src/services/httpRequest';

interface ResetPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

// Define the expected location state type
interface LocationState {
  email: string;
  otpToken: string;
}

function ResetPasswordPage() {
  const { state: townshipState, township, uuid } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    newPassword: '',
    confirmPassword: ''
  });
  const [validation, setValidation] = useState<PasswordValidation>({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Get email and token from navigation state with proper typing
  const locationState = location.state as LocationState | null;
  const email = locationState?.email;
  const otpToken = locationState?.otpToken;

  // Redirect if no email or token provided
  useEffect(() => {
    if (!email || !otpToken) {
      navigate(`/${townshipState}/${township}/${uuid}/forgot-password`, {
        replace: true
      });
    }
  }, [email, otpToken, navigate]);

  // Password validation
  useEffect(() => {
    const password = formData.newPassword;
    setValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/.test(password)
    });
  }, [formData.newPassword]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const isPasswordValid = () => {
    return Object.values(validation).every(Boolean);
  };

  const isFormValid = () => {
    return (
      isPasswordValid() &&
      formData.newPassword === formData.confirmPassword &&
      formData.newPassword.length > 0 &&
      formData.confirmPassword.length > 0
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid()) {
      setError('Password does not meet the requirements');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await httpRequest('PATCH', `/auth/updatePassword/${townshipState}/${township}/${uuid}`, {
        Password: formData.newPassword,
        ResetToken: otpToken
      });

      if (response?.status === 'success') {
        setSuccess('Password has been reset successfully!');
        // Navigate to login page after success
        setTimeout(() => {
          navigate(`/${townshipState}/${township}/${uuid}/login`, {
            replace: true,
            state: {
              message:
                'Password reset successfully. Please login with your new password.'
            }
          });
        }, 2000);
      } else {
        setError(
          response?.message || 'Failed to reset password. Please try again.'
        );
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToOTP = () => {
    navigate(
      `/${townshipState}/${township}/${uuid}/forgot-password/verify-otp`,
      {
        state: { email: email }
      }
    );
  };

  const getValidationColor = (isValid: boolean) => {
    return isValid ? 'success.main' : 'text.secondary';
  };

  const getValidationIcon = (isValid: boolean) => {
    return isValid ? '✓' : '○';
  };

  return (
    <Box
      sx={{
        minHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <IconButton
          onClick={handleBackToOTP}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.light'
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, color: 'primary.main' }}>
          Back
        </Typography>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          mt: 8
        }}
      >
        <Container maxWidth="sm">
          <Card
            sx={{
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              overflow: 'visible'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <form onSubmit={handleSubmit}>
                <Box sx={{ p: 4, pb: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    Reset Password
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 1 }}
                  >
                    Create a new password for your account
                  </Typography>

                  <Typography
                    variant="body2"
                    color="primary.main"
                    align="center"
                    sx={{ mb: 3, fontWeight: 'medium' }}
                  >
                    {email}
                  </Typography>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: '8px',
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: '8px',
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                    >
                      {success}
                    </Alert>
                  )}

                  {/* New Password Field */}
                  <TextField
                    autoFocus
                    name="newPassword"
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          component="span"
                          sx={{
                            color: 'action.active',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              width="18"
                              height="11"
                              x="3"
                              y="11"
                              rx="2"
                              ry="2"
                            />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </Box>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                            sx={{ mr: -0.5 }}
                          >
                            {showNewPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  {/* Password Requirements */}
                  {formData.newPassword.length > 0 && (
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 'medium' }}
                      >
                        Password Requirements:
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: getValidationColor(validation.minLength),
                            fontSize: '0.8rem'
                          }}
                        >
                          {getValidationIcon(validation.minLength)} At least 8
                          characters
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: getValidationColor(validation.hasUppercase),
                            fontSize: '0.8rem'
                          }}
                        >
                          {getValidationIcon(validation.hasUppercase)} At least
                          one uppercase letter
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: getValidationColor(validation.hasLowercase),
                            fontSize: '0.8rem'
                          }}
                        >
                          {getValidationIcon(validation.hasLowercase)} At least
                          one lowercase letter
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: getValidationColor(validation.hasNumber),
                            fontSize: '0.8rem'
                          }}
                        >
                          {getValidationIcon(validation.hasNumber)} At least one
                          number
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: getValidationColor(
                              validation.hasSpecialChar
                            ),
                            fontSize: '0.8rem'
                          }}
                        >
                          {getValidationIcon(validation.hasSpecialChar)} At
                          least one special character
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Confirm Password Field */}
                  <TextField
                    name="confirmPassword"
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    error={
                      formData.confirmPassword.length > 0 &&
                      formData.newPassword !== formData.confirmPassword
                    }
                    helperText={
                      formData.confirmPassword.length > 0 &&
                      formData.newPassword !== formData.confirmPassword
                        ? 'Passwords do not match'
                        : ''
                    }
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          component="span"
                          sx={{
                            color: 'action.active',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              width="18"
                              height="11"
                              x="3"
                              y="11"
                              rx="2"
                              ry="2"
                            />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </Box>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                            sx={{ mr: -0.5 }}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>

                <Box sx={{ p: 4, pt: 0 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !isFormValid()}
                    fullWidth
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Reset Password'
                    )}
                  </Button>

                  <Button
                    onClick={handleBackToOTP}
                    disabled={loading}
                    variant="text"
                    fullWidth
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Back
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
}

export default ResetPasswordPage;
